import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Users, User, ChevronRight, Pencil, Trash2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tinkah_token")}` }
});

const measureLabels = {
  shoulder: "Shoulder", bust: "Bust", hips: "Hips", length: "Length",
  sleeve_length: "Sleeve", armhole: "Armhole", cuff: "Cuff",
  button_placement: "Button", sheila_length: "Sheila",
};

const defaultMeasurements = {
  shoulder: 0, bust: 0, hips: 0, length: 0, sleeve_length: 0,
  armhole: 0, cuff: 0, button_placement: 0, sheila_length: 0,
};

export default function CustomersPage({ user }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  
  const isSuperAdmin = user?.role === "super_admin";
  const [selectedParent, setSelectedParent] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  const [form, setForm] = useState({ name: "", contact: "", address: "", measurements: { ...defaultMeasurements } });
  const [subForm, setSubForm] = useState({ name: "", relationship: "", measurements: { ...defaultMeasurements } });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data);
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, form);
        toast.success("Customer added");
      }
      setDialogOpen(false);
      setForm({ name: "", contact: "", address: "", measurements: { ...defaultMeasurements } });
      setEditing(null);
      loadCustomers();
    } catch (err) {
      toast.error("Failed to save customer");
    }
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    if (!subForm.name.trim() || !selectedParent) return;
    try {
      if (editingSub) {
        await axios.put(`${API}/customers/${selectedParent.id}/sub-profiles/${editingSub.id}`, subForm);
        toast.success("Sub-profile updated");
      } else {
        await axios.post(`${API}/customers/${selectedParent.id}/sub-profiles`, subForm);
        toast.success("Sub-profile added");
      }
      setSubDialogOpen(false);
      setSubForm({ name: "", relationship: "", measurements: { ...defaultMeasurements } });
      setEditingSub(null);
      loadCustomers();
    } catch (err) {
      toast.error("Failed to save sub-profile");
    }
  };

  const handleDelete = async (id) => {
    if (!isSuperAdmin) return;
    if (!window.confirm("Delete this customer?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`, getAuthHeaders());
      toast.success("Customer deleted");
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete customer");
    }
  };

  const handleDeleteSub = async (customerId, subId) => {
    if (!isSuperAdmin) return;
    if (!window.confirm("Delete this sub-profile?")) return;
    try {
      await axios.delete(`${API}/customers/${customerId}/sub-profiles/${subId}`, getAuthHeaders());
      toast.success("Sub-profile deleted");
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete sub-profile");
    }
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setForm({
      name: customer.name,
      contact: customer.contact || "",
      address: customer.address || "",
      measurements: customer.measurements || { ...defaultMeasurements },
    });
    setDialogOpen(true);
  };

  const openAddSub = (customer) => {
    setSelectedParent(customer);
    setEditingSub(null);
    setSubForm({ name: "", relationship: "", measurements: { ...defaultMeasurements } });
    setSubDialogOpen(true);
  };

  const openEditSub = (customer, sub) => {
    setSelectedParent(customer);
    setEditingSub(sub);
    setSubForm({
      name: sub.name,
      relationship: sub.relationship || "",
      measurements: sub.measurements || { ...defaultMeasurements },
    });
    setSubDialogOpen(true);
  };

  const filtered = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.contact?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-['Space_Mono'] text-3xl tracking-tight uppercase text-[#5C4D24]">Customers</h2>
          <p className="text-sm text-[#8B7536] mt-1">Manage customer profiles & groups</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ name: "", contact: "", address: "", measurements: { ...defaultMeasurements } }); } }}>
          <DialogTrigger asChild>
            <button
              data-testid="add-customer-btn"
              className="flex items-center gap-2 px-6 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95"
            >
              <Plus className="w-4 h-4" /> Add Customer
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-none border-[#D6D6CF]">
            <DialogHeader>
              <DialogTitle className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">
                {editing ? "Edit Customer" : "New Customer"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                  data-testid="customer-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Contact</Label>
                  <Input
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                    data-testid="customer-contact-input"
                  />
                </div>
                <div>
                  <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Address</Label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                    data-testid="customer-address-input"
                  />
                </div>
              </div>
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536] mb-2 block">Measurements (cm)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(measureLabels).map(([key, label]) => (
                    <div key={key}>
                      <Label className="text-[10px] text-[#8B7536]">{label}</Label>
                      <Input
                        type="number"
                        value={form.measurements[key] || 0}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            measurements: { ...form.measurements, [key]: parseFloat(e.target.value) || 0 },
                          })
                        }
                        className="mt-0.5 rounded-none border-[#D6D6CF] font-['Space_Mono'] text-sm h-8"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="px-4 py-2 border border-[#D6D6CF] text-[#5C4D24] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#F5F5F0] transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-customer-btn"
                  className="px-4 py-2 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all"
                >
                  {editing ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B7536]" />
        <Input
          placeholder="Search customers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-none border-[#D6D6CF] font-['Space_Mono'] text-sm"
          data-testid="customer-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center bg-white border border-[#D6D6CF]">
          <Users className="w-12 h-12 text-[#D6D6CF] mx-auto mb-4" />
          <p className="text-[#8B7536]">No customers found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((customer) => (
            <div key={customer.id} className="bg-white border border-[#D6D6CF] overflow-hidden" data-testid={`customer-card-${customer.id}`}>
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                onClick={() => setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#F5F5F0] border border-[#D6D6CF] flex items-center justify-center">
                    <User className="w-5 h-5 text-[#9F7D23]" />
                  </div>
                  <div>
                    <p className="font-['Space_Mono'] text-sm text-[#5C4D24]">{customer.name}</p>
                    <p className="text-xs text-[#8B7536]">{customer.contact || "No contact"}</p>
                  </div>
                  {customer.sub_profiles?.length > 0 && (
                    <span className="px-2 py-0.5 bg-[#D6AD60]/20 text-[#5C4D24] text-[10px] font-['Space_Mono'] tracking-widest uppercase">
                      {customer.sub_profiles.length} sub-profile{customer.sub_profiles.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); openAddSub(customer); }}
                    className="p-2 text-[#9F7D23] hover:bg-[#F5F5F0] transition-colors"
                    title="Add sub-profile"
                    data-testid={`add-sub-btn-${customer.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEdit(customer); }}
                    className="p-2 text-[#8B7536] hover:bg-[#F5F5F0] transition-colors"
                    data-testid={`edit-customer-btn-${customer.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {isSuperAdmin && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(customer.id); }}
                      className="p-2 text-[#9F3A3A] hover:bg-[#F5F5F0] transition-colors"
                      data-testid={`delete-customer-btn-${customer.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronRight className={`w-4 h-4 text-[#8B7536] transition-transform ${expandedCustomer === customer.id ? "rotate-90" : ""}`} />
                </div>
              </div>

              {expandedCustomer === customer.id && (
                <div className="border-t border-[#D6D6CF] bg-[#FAFAF8] p-4 animate-fade-in">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] mb-1">Address</p>
                      <p className="text-sm text-[#5C4D24]">{customer.address || "Not provided"}</p>
                    </div>
                    <div>
                      <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] mb-1">Measurements</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(measureLabels).slice(0, 5).map(([key, label]) => (
                          <span key={key} className="text-xs text-[#5C4D24]">
                            {label}: <strong>{customer.measurements?.[key] || 0}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {customer.sub_profiles?.length > 0 && (
                    <div>
                      <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] mb-2">Sub-Profiles</p>
                      <div className="space-y-2">
                        {customer.sub_profiles.map((sub) => (
                          <div key={sub.id} className="flex items-center justify-between bg-white border border-[#D6D6CF] p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#D6AD60]/10 border border-[#D6AD60] flex items-center justify-center">
                                <User className="w-4 h-4 text-[#9F7D23]" />
                              </div>
                              <div>
                                <p className="text-sm text-[#5C4D24]">{sub.name}</p>
                                {sub.relationship && <p className="text-xs text-[#8B7536]">{sub.relationship}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditSub(customer, sub)}
                                className="p-1.5 text-[#8B7536] hover:bg-[#F5F5F0] transition-colors"
                                data-testid={`edit-sub-btn-${sub.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              {isSuperAdmin && (
                                <button
                                  onClick={() => handleDeleteSub(customer.id, sub.id)}
                                  className="p-1.5 text-[#9F3A3A] hover:bg-[#F5F5F0] transition-colors"
                                  data-testid={`delete-sub-btn-${sub.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sub-profile Dialog */}
      <Dialog open={subDialogOpen} onOpenChange={(open) => { setSubDialogOpen(open); if (!open) { setEditingSub(null); setSelectedParent(null); } }}>
        <DialogContent className="max-w-lg rounded-none border-[#D6D6CF]">
          <DialogHeader>
            <DialogTitle className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">
              {editingSub ? "Edit Sub-Profile" : "Add Sub-Profile"}
              {selectedParent && <span className="text-[#9F7D23] text-sm ml-2">({selectedParent.name})</span>}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Name *</Label>
                <Input
                  value={subForm.name}
                  onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                  className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                  data-testid="sub-name-input"
                />
              </div>
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Relationship</Label>
                <Select value={subForm.relationship} onValueChange={(v) => setSubForm({ ...subForm, relationship: v })}>
                  <SelectTrigger className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']" data-testid="sub-relationship-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Daughter">Daughter</SelectItem>
                    <SelectItem value="Sister">Sister</SelectItem>
                    <SelectItem value="Mother">Mother</SelectItem>
                    <SelectItem value="Friend">Friend</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536] mb-2 block">Measurements (cm)</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(measureLabels).map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-[10px] text-[#8B7536]">{label}</Label>
                    <Input
                      type="number"
                      value={subForm.measurements[key] || 0}
                      onChange={(e) =>
                        setSubForm({
                          ...subForm,
                          measurements: { ...subForm.measurements, [key]: parseFloat(e.target.value) || 0 },
                        })
                      }
                      className="mt-0.5 rounded-none border-[#D6D6CF] font-['Space_Mono'] text-sm h-8"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSubDialogOpen(false)}
                className="px-4 py-2 border border-[#D6D6CF] text-[#5C4D24] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#F5F5F0] transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="save-sub-btn"
                className="px-4 py-2 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all"
              >
                {editingSub ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
